import React, { useState, useEffect, useMemo } from 'react';
import {
    Typography, Grid2, Select, MenuItem, Alert, AlertTitle,
    TableBody, TableCell, TableContainer, Table, TableHead, TableRow, TableSortLabel, Button
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useNavigate } from 'react-router-dom';
import config from '../../config/config.js';
import { makeGetRequestToUrl } from '../commonFunc.jsx';

import newUser from '../../assets/icons/newUserIcon.svg';
import totalRideIcon from '../../assets/icons/vehicle.svg';
import increment from '../../assets/icons/increment.svg';
import decrement from '../../assets/icons/decrement.svg';
import CancelledRideIcon from '../../assets/icons/cancelledRide.svg';
import activeRidesIcon from '../../assets/icons/activeRides.svg';
import earningsIcon from '../../assets/icons/earningsIcon.svg';
import prevIcon from '../../assets/icons/left-arrow.svg';
import nextIcon from '../../assets/icons/right-arrow.svg';
import { taxiEngagementStatus, taxiEngagementStatusReverse } from '../../constant.js';

// Card styles cycle through the same palette used in MainDashboard
const CARD_STYLES = [
    { bg: '#F3F8FF', icon: totalRideIcon },
    { bg: '#FFF5F5', icon: CancelledRideIcon },
    { bg: '#FFFBF0', icon: activeRidesIcon },
    { bg: '#EFFBF7', icon: earningsIcon },
    { bg: '#F3F8FF', icon: newUser },
];

const CURRENCY_KEYS = ['earnings', 'wallet'];

const FleetDashboard = () => {
    const serverUrl = config.serverUrl;
    const navigate = useNavigate();

    const cities = JSON.parse(localStorage.getItem('cities') || '[]');

    const [period, setPeriod] = useState(1);
    const [cityFilter, setCityFilter] = useState(cities.length > 0 ? cities[0].city_id : '');
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tokenExpired, setTokenExpired] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Recent rides table state
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('id');
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const rowsPerPage = 10;

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const url = `${serverUrl}/dashboard/fleet_card_details`;
            const obj = { token, timestamp: new Date().getTime(), period };
            if (cityFilter) obj.city_id = cityFilter;

            const response = await makeGetRequestToUrl(url, obj);

            if (response.flag === 200 && response.data) {
                setDashboardData(response.data);
                setRows(response.data.recentRides || []);
                setPage(0);
            } else if (response.flag === 102) {
                localStorage.removeItem('token');
                setErrorMessage('Your session has expired. Please log in again.');
                setTokenExpired(true);
                setTimeout(() => navigate('/signup'), 2000);
            } else {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
        } catch (error) {
            console.error('Error fetching fleet dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, [period, cityFilter]);

    const { cardDetails, earningsChart, alerts } = dashboardData || {};

    const chartXAxis = earningsChart ? earningsChart.map((i) => i.date) : [];
    const onlineEarnings = earningsChart ? earningsChart.map((i) => i.online) : [];
    const cashEarnings = earningsChart ? earningsChart.map((i) => i.cash) : [];
    const totalEarnings = earningsChart ? earningsChart.map((i) => i.total) : [];

    // ---- table helpers ----
    const handleRequestSort = (property) => {
        const isAscending = orderBy === property && order === 'asc';
        setOrder(isAscending ? 'desc' : 'asc');
        setOrderBy(property);
        const sorted = [...rows].sort((a, b) => {
            if (a[property] < b[property]) return isAscending ? -1 : 1;
            if (a[property] > b[property]) return isAscending ? 1 : -1;
            return 0;
        });
        setRows(sorted);
    };

    const paginatedData = useMemo(
        () => (Array.isArray(rows) ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : []),
        [rows, page]
    );
    const totalPages = Math.ceil(rows.length / rowsPerPage);
    const handleChangePage = (_, newPage) => setPage(newPage);

    // ---- card value helpers (supports plain values OR { count, change } objects) ----
    const getCardValue = (val) => (val !== null && typeof val === 'object' ? val.count : val);
    const getCardChange = (val) => (val !== null && typeof val === 'object' ? val.change : undefined);

    const getStatusLabel = (status) => {
        if (status === null || status === undefined || status === '') return 'N/A';
        const key =
            taxiEngagementStatusReverse?.[status] ||
            (taxiEngagementStatus && Object.keys(taxiEngagementStatus).find(
                (k) => taxiEngagementStatus[k] === Number(status)
            )) ||
            status;
        return typeof key === 'string' ? key.replaceAll('_', ' ') : String(key);
    };

    const statusColors = (status) => {
        const raw =
            taxiEngagementStatusReverse?.[status] ||
            (typeof status === 'string' && taxiEngagementStatus?.[status.toUpperCase()] !== undefined
                ? status
                : status);
        const s = String(raw || '').toLowerCase();
        if (['completed', 'ended', 'success'].some((term) => s.includes(term))) return { bg: '#E7F8F2', color: '#0DBA78' };
        if (['cancel', 'reject', 'missed', 'failed', 'timeout', 'no_show', 'no show'].some((term) => s.includes(term))) return { bg: '#F9E9E9', color: '#D14545' };
        if (['started', 'arrived', 'accepted', 'alive'].some((term) => s.includes(term))) return { bg: '#E8F1FD', color: '#0065FF' };
        return { bg: '#FFFBF0', color: '#E0A100' };
    };

    if (loading && !dashboardData) {
        return <Typography padding={3}>Loading Dashboard...</Typography>;
    }

    return (
        <div>
            {tokenExpired && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

            {/* Header */}
            <Grid2 container>
                <Typography style={{ fontSize: '28px', fontWeight: '500' }}>Fleet Dashboard</Typography>
                <Grid2 item sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                    {cities.length > 0 && (
                        <Select
                            displayEmpty
                            value={cityFilter}
                            onChange={(e) => setCityFilter(e.target.value)}
                            style={{ width: '180px', height: '42px', borderRadius: '8px', backgroundColor: '#FFFFFF' }}
                        >
                            <MenuItem value="" disabled>Select City</MenuItem>
                            {cities.map((city) => (
                                <MenuItem key={city.city_id} value={city.city_id}>{city.city_name}</MenuItem>
                            ))}
                        </Select>
                    )}
                </Grid2>
            </Grid2>

            {/* Alerts */}
            {alerts && (alerts.driver_docs_expiring > 0 || alerts.vehicle_docs_expiring > 0 || alerts.drivers_pending_verification > 0) && (
                <Grid2 className="mainBox" sx={{ marginTop: '1%' }}>
                    <Grid2 container spacing={2}>
                        {alerts.driver_docs_expiring > 0 && (
                            <Grid2 item size={4}>
                                <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                                    <AlertTitle>Driver Docs Expiring</AlertTitle>
                                    You have <strong>{alerts.driver_docs_expiring}</strong> driver(s) with expiring documents.
                                </Alert>
                            </Grid2>
                        )}
                        {alerts.vehicle_docs_expiring > 0 && (
                            <Grid2 item size={4}>
                                <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                                    <AlertTitle>Vehicle Docs Expiring</AlertTitle>
                                    You have <strong>{alerts.vehicle_docs_expiring}</strong> vehicle(s) with expiring documents.
                                </Alert>
                            </Grid2>
                        )}
                        {alerts.drivers_pending_verification > 0 && (
                            <Grid2 item size={4}>
                                <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                    <AlertTitle>Pending Verification</AlertTitle>
                                    <strong>{alerts.drivers_pending_verification}</strong> driver(s) pending verification.
                                </Alert>
                            </Grid2>
                        )}
                    </Grid2>
                </Grid2>
            )}

            {/* Cards */}
            <Grid2 className="mainBox" sx={{ marginTop: '1%' }}>
                <Grid2 sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Grid2 item>
                        <select
                            style={{ minWidth: '180px', padding: '1%', borderRadius: '10px', minHeight: '42px' }}
                            value={period}
                            onChange={(e) => setPeriod(Number(e.target.value))}
                        >
                            <option value={1}>Today</option>
                            <option value={2}>Last 7 Days</option>
                            <option value={3}>Last 30 Days</option>
                        </select>
                    </Grid2>
                </Grid2>

                <Grid2
                    container
                    sx={{
                        marginTop: '1%',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '20px',
                    }}
                >
                    {cardDetails && Object.keys(cardDetails).map((key, index) => {
                        const style = CARD_STYLES[index % CARD_STYLES.length];
                        const value = getCardValue(cardDetails[key]);
                        const change = getCardChange(cardDetails[key]);
                        const isCurrency = CURRENCY_KEYS.some((k) => key.includes(k));

                        return (
                            <Grid2
                                item
                                key={key}
                                sx={{
                                    backgroundColor: style.bg,
                                    padding: '7% 4%',
                                    borderRadius: '12px',
                                    minHeight: '150px',
                                }}
                            >
                                <img src={style.icon} alt={key} />
                                <Typography sx={{ fontSize: '16px', color: '#1A1E25', textTransform: 'capitalize' }}>
                                    {key.replace(/_/g, ' ')}
                                </Typography>
                                <Typography sx={{ fontSize: '24px', color: '#1A1E25', fontWeight: '600', marginTop: '4px' }}>
                                    {isCurrency ? '₹' : ''}{value ?? 0}
                                </Typography>

                                {change !== undefined && (
                                    <Grid2 item sx={{ display: 'flex', alignItems: 'center' }}>
                                        <img src={change >= 0 ? increment : decrement} alt={change >= 0 ? 'increment' : 'decrement'} />
                                        <Typography
                                            sx={{
                                                color: change >= 0 ? '#0DBA78' : '#D14545',
                                                fontSize: '12px',
                                                marginTop: '8px',
                                                marginLeft: '4px',
                                            }}
                                        >
                                            {change}%
                                        </Typography>
                                    </Grid2>
                                )}
                            </Grid2>
                        );
                    })}
                </Grid2>
            </Grid2>

            {/* Earnings chart */}
            <Grid2 className="mainBox" sx={{ marginTop: '1%' }}>
                <Grid2 item container size={12}>
                    <Grid2 item size={6}>
                        <Typography sx={{ fontSize: '24px', color: '#1A1E25', paddingTop: '1%' }}>Earnings Overview</Typography>
                    </Grid2>
                </Grid2>
                <hr />

                <Grid2 item>
                    {chartXAxis.length > 0 ? (
                        <LineChart
                            xAxis={[{ data: chartXAxis, scaleType: 'point' }]}
                            series={[
                                { data: totalEarnings, label: 'Total Earnings', color: '#2563eb' },
                                { data: onlineEarnings, label: 'Online Earnings', color: '#16a34a' },
                                { data: cashEarnings, label: 'Cash Earnings', color: '#ea580c' },
                            ]}
                            height={350}
                        />
                    ) : (
                        <Typography sx={{ padding: '16px', color: '#83888E' }}>No chart data available.</Typography>
                    )}

                    <Grid2 container spacing={4} sx={{ backgroundColor: '#F9F9F9', justifyContent: 'center', padding: '2px' }}>
                        {[
                            { label: 'Total Earnings', color: '#2563eb' },
                            { label: 'Online Earnings', color: '#16a34a' },
                            { label: 'Cash Earnings', color: '#ea580c' },
                        ].map((item) => (
                            <Grid2 item key={item.label} sx={{ display: 'flex' }}>
                                <Grid2 item>
                                    <FiberManualRecordIcon style={{ fontSize: '16px', color: item.color }} />
                                </Grid2>
                                <Grid2 item>
                                    <Typography sx={{ color: '#83888E', fontSize: '14px' }}>{item.label}</Typography>
                                </Grid2>
                            </Grid2>
                        ))}
                    </Grid2>
                </Grid2>
            </Grid2>

            {/* Recent rides */}
            <Grid2 className="mainBox" sx={{ marginTop: '1%' }}>
                <Grid2 item>
                    <Typography sx={{ fontSize: '24px', color: '#1A1E25', paddingTop: '1%' }}>Recent Rides</Typography>
                </Grid2>
                <hr />

                <Grid2 item>
                    <TableContainer className="table-container">
                        <Table stickyHeader className="scrollable-table">
                            <TableHead>
                                <TableRow>
                                    <TableCell component="th" align="center" className="table-header" style={{ width: '10%' }}>
                                        <TableSortLabel
                                            active={orderBy === 'id'}
                                            direction={orderBy === 'id' ? order : 'asc'}
                                            onClick={() => handleRequestSort('id')}
                                        >
                                            Ride ID
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="center" className="table-header" style={{ width: '25%' }}>Driver</TableCell>
                                    <TableCell align="center" className="table-header" style={{ width: '15%' }}>Status</TableCell>
                                    <TableCell align="center" className="table-header" style={{ width: '15%' }}>Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedData.length > 0 ? (
                                    paginatedData.map((ride, idx) => {
                                        const sc = statusColors(ride.status);
                                        return (
                                            <TableRow key={ride.id ?? idx}>
                                                <TableCell>{ride.engagement_id || 'N/A'}</TableCell>
                                                <TableCell>{ride.driver_name || 'N/A'}</TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="contained"
                                                        style={{
                                                            backgroundColor: sc.bg,
                                                            color: sc.color,
                                                            textTransform: 'none',
                                                            pointerEvents: 'none',
                                                            borderRadius: '6px',
                                                            minWidth: '60px',
                                                            border: 'none',
                                                            boxShadow: 'none',
                                                            fontFamily: 'Poppins',
                                                            marginRight: '16px',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        <FiberManualRecordIcon style={{ fontSize: '16px' }} /> {getStatusLabel(ride.status)}
                                                    </Button>
                                                </TableCell>
                                                <TableCell>{ride.amount != null ? `₹${ride.amount}` : 'N/A'}</TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">No recent rides found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {rows.length > 0 && (
                            <div className="custom-pagination">
                                <div className="pagination-info">
                                    {`Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, rows.length)} of ${rows.length} entries`}
                                </div>
                                <div className="pagination-controls">
                                    <button
                                        onClick={() => handleChangePage(null, page - 1)}
                                        disabled={page === 0}
                                        className="pagination-arrow"
                                    >
                                        <img src={prevIcon} alt="Previous" />
                                    </button>

                                    <div className="pagination-numbers">
                                        {page > 1 && (
                                            <>
                                                <button onClick={() => handleChangePage(null, 0)} className="pagination-number">1</button>
                                                <span className="pagination-dots">...</span>
                                            </>
                                        )}
                                        {[...Array(totalPages)]
                                            .map((_, index) => index)
                                            .filter((index) => index >= page - 1 && index <= page + 1)
                                            .map((index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleChangePage(null, index)}
                                                    className={`pagination-number ${page === index ? 'active' : ''}`}
                                                >
                                                    {index + 1}
                                                </button>
                                            ))}
                                        {page < totalPages - 2 && (
                                            <>
                                                <span className="pagination-dots">...</span>
                                                <button
                                                    onClick={() => handleChangePage(null, totalPages - 1)}
                                                    className="pagination-number"
                                                >
                                                    {totalPages}
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => handleChangePage(null, page + 1)}
                                        disabled={page >= totalPages - 1}
                                        className="pagination-arrow"
                                    >
                                        <img src={nextIcon} alt="Next" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </TableContainer>
                </Grid2>
            </Grid2>
        </div>
    );
};

export default FleetDashboard;